import { useState } from 'react'
import { X, Trash2, AlertTriangle, Loader2, File, Image, FileText } from 'lucide-react'
import toast from 'react-hot-toast'

interface AttachmentInfo {
  id: string
  name: string
  webViewLink: string
  type?: string
  size?: number
}

interface AttachmentDeletionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  attachments: AttachmentInfo[]
}

export default function AttachmentDeletionModal({
  isOpen,
  onClose,
  onConfirm,
  attachments
}: AttachmentDeletionModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    try {
      setLoading(true)
      setError(null)
      
      await onConfirm()
      
      toast.success(`${attachments.length} anexo(s) excluído(s) com sucesso!`, {
        duration: 3000,
        position: 'top-right',
      })
      
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir anexos')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setError(null)
      onClose()
    }
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
      return Image
    } else if (['pdf'].includes(extension || '')) {
      return FileText
    } else {
      return File
    }
  }

  const formatFileSize = (bytes?: number): string => {
    if (bytes == null) return '--- KB'
    if (bytes === 0) return '0 KB'
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-lg transform rounded-lg bg-white p-6 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              <h2 className="text-lg font-semibold text-brand-text-primary">
                Confirmar Exclusão de Anexos
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="rounded-full p-1 text-gray-400 hover:bg-brand-surface-100 hover:text-brand-text-secondary disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Alerta sobre irreversibilidade */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Atenção: Exclusão Permanente
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    Esta ação excluirá permanentemente os arquivos do Google Drive e não poderá ser desfeita.
                  </p>
                </div>
              </div>
            </div>

            {/* Informações sobre os arquivos */}
            <div className="bg-brand-surface-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-brand-text-primary mb-3">
                {attachments.length} anexo(s) serão excluídos:
              </p>
              
              {/* Lista de arquivos */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {attachments.map((attachment) => {
                  const FileIcon = getFileIcon(attachment.name)
                  return (
                    <div key={attachment.id} className="flex items-center gap-3 p-2 bg-white rounded border border-brand-neutral-200">
                      <FileIcon className="h-4 w-4 text-brand-text-muted flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-brand-text-primary truncate">
                          {attachment.name}
                        </p>
                        <p className="text-xs text-brand-text-muted">
                          {formatFileSize(attachment.size)}
                        </p>
                      </div>
                      <a
                        href={attachment.webViewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary-500 hover:text-brand-primary-600 text-xs underline flex-shrink-0"
                        title="Visualizar arquivo"
                      >
                        Visualizar
                      </a>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Pergunta de confirmação */}
            <div className="text-sm text-brand-text-secondary">
              <p>Tem certeza de que deseja excluir permanentemente estes arquivos?</p>
            </div>

            {/* Erro */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Botões */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-4 py-2 text-sm font-medium text-brand-text-primary bg-white border border-brand-neutral-300 rounded-md hover:bg-brand-surface-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Excluindo...
                  </div>
                ) : (
                  'Confirmar Exclusão'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}